// The big moments of a fight, said once and large in the middle of the
// view: a mast gone by the board, canvas in ribbons, colours struck, a prize
// taken, a ship blown up. One at a time; a queue behind it, the least of
// them dropped if it grows long, so the moment a thing is said stays close
// to the moment it happened.

export type CalloutKind = 'good' | 'bad' | '';

interface Callout {
  title: string;
  sub: string;
  kind: CalloutKind;
  /** Larger moments go ahead of smaller ones waiting. */
  weight: number;
}

/** How long each is shown, s (the fade in and out included). */
const SHOW = 3.2;
const BRIEF = 2.2;

export class Callouts {
  private readonly el: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly subEl: HTMLElement;
  private readonly queue: Callout[] = [];
  private current: Callout | null = null;
  private age = 0;
  private rest = 0;

  constructor(wrap: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'callout';
    this.titleEl = document.createElement('div');
    this.titleEl.className = 'callout-title';
    this.subEl = document.createElement('div');
    this.subEl.className = 'callout-sub';
    this.el.append(this.titleEl, this.subEl);
    wrap.appendChild(this.el);
  }

  /** Say it (after whatever is being said now). `weight` 2 the great moments, 1 the lesser. */
  show(title: string, sub = '', kind: CalloutKind = '', weight = 1): void {
    if (this.current?.title === title || this.queue.some((c) => c.title === title)) return;
    this.queue.push({ title, sub, kind, weight });
    this.queue.sort((a, b) => b.weight - a.weight);
    // Old news isn't news: keep the queue short.
    while (this.queue.length > 3) this.queue.pop();
  }

  /** The one up now (for tests and the dev log). */
  get showing(): string {
    return this.current?.title ?? '';
  }

  update(dt: number): void {
    this.rest -= dt;
    if (this.current) {
      this.age += dt;
      // Cut short when something waits behind it.
      const life = this.queue.length ? BRIEF : SHOW;
      if (this.age >= life) {
        this.current = null;
        this.el.classList.remove('on');
        // Let it fade before the next comes in.
        this.rest = 0.4;
      }
    }
    if (!this.current && this.queue.length && this.rest <= 0) {
      this.current = this.queue.shift()!;
      this.age = 0;
      this.titleEl.textContent = this.current.title;
      this.subEl.textContent = this.current.sub;
      this.subEl.hidden = !this.current.sub;
      this.el.className = `callout ${this.current.kind} w${this.current.weight}`;
      // Restart the entrance (a reflow between the class changes).
      void this.el.offsetWidth;
      this.el.classList.add('on');
    }
  }
}
